import type { jobEnity } from '@/api/job';
import * as jobApi from '@/api/job';
import { jobStateEnum } from '@/api/job';
import { FolderOpenOutlined, ReloadOutlined } from '@ant-design/icons';
import { App, Button, DatePicker, Form, Input, Select, Space, Table, TableProps, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import ClusterOverviewPanel from '@/components/clusterOverviewPanel';
import * as clusterApi from '@/api/cluster';
import type { ClusterView } from '@/api/cluster';

type JobListFormType = {
    cluster?: string;
    jobName?: string;
    startTime?: string;
    endTime?: string;
    state?: string;
}

const JobList = () => {
    const { message } = App.useApp();
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const [data, setData] = useState<jobEnity[]>([]);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [form] = Form.useForm<JobListFormType>();
    const [clusters, setClusters] = useState<ClusterView[]>([]);
    const [clusterId, setClusterId] = useState<string>('all');
    // With a single cluster there is nothing to choose, and the selector would only add a
    // step. The backend's default (no cluster parameter) stays the local accounting database,
    // which is exactly the old behaviour for a deployment without remote clusters.
    const showClusterPicker = clusters.length > 1;

    const fetchData = async (params = {}) => {
        const res = await jobApi.getJobs({ page: pagination.current, pageSize: pagination.pageSize, ...params });
        if (res?.ok && res.data) {
            setData(res.data?.jobs);
            setTotal(res.data?.total);
        } else {
            message.error(t('job_list.fetch_failed'));
        }
    };

    useEffect(() => {
        clusterApi.getClusters().then(res => {
            if (res?.ok && res.data) setClusters(res.data);
        });
    }, []);

    useEffect(() => {
        form.validateFields().then(values => {
            fetchData({
                ...values,
                page: pagination.current,
                pageSize: pagination.pageSize,
                cluster: showClusterPicker ? clusterId : undefined,
            });
        });
    }, [pagination, clusterId, showClusterPicker]);

    const handleTableChange: TableProps<any>['onChange'] = (pagination) => {
        if (!pagination.current) return;
        if (!pagination.pageSize) return;
        setPagination({ current: pagination.current, pageSize: pagination.pageSize, });
    };

    const onFinish = (values: JobListFormType) => {
        fetchData({
            ...values,
            page: pagination.current,
            pageSize: pagination.pageSize,
            cluster: showClusterPicker ? clusterId : undefined,
            startTime: values.startTime ? dayjs(values.startTime).unix() : undefined,
            endTime: values.endTime ? dayjs(values.endTime).unix() : undefined,
        });
    };

    // The result retrieval happens after the job reaches a terminal state, so "completed" and
    // "files are here" are different facts. Showing the staging state is what makes a failed
    // pull visible instead of looking like a job that produced nothing.
    const renderResults = (record: jobEnity) => {
        const state = record.stagingState;
        if (!state || state === 'skipped' || state === 'none') {
            return <>-</>;
        }
        const map: Record<string, { color: string; text: string }> = {
            pushing: { color: 'blue', text: t('job_list.results_pending') },
            pushed: { color: 'blue', text: t('job_list.results_pending') },
            pulling: { color: 'blue', text: t('job_list.results_pending') },
            pulled: { color: 'green', text: t('job_list.results_retrieved') },
            failed: { color: 'red', text: t('job_list.results_failed') },
        };
        const info = map[state] ?? { color: 'default', text: state };
        const detail = record.stagingMessage || record.lastError;
        const tag = <Tag color={info.color}>{info.text}</Tag>;
        return detail ? <Tooltip title={detail}>{tag}</Tooltip> : tag;
    };

    const jobStateMap: { [key: number]: { color: string, text: string } } = {
        [jobStateEnum.PENDING]: {
            color: "#a6accd",
            text: "PENDING"
        },
        [jobStateEnum.RUNNING]: {
            color: "#ebb563",
            text: "RUNNING"
        },
        [jobStateEnum.SUSPENDED]: {
            color: "#c2b36e",
            text: "SUSPENDED"
        },
        [jobStateEnum.COMPLETE]: {
            color: "#67c23a",
            text: "COMPLETE"
        },
        [jobStateEnum.CANCELED]: {
            color: "#a6accd",
            text: "CANCELED"
        },
        [jobStateEnum.FAILED]: {
            color: "#bc6066",
            text: "FAILED"
        },
        [jobStateEnum.TIMEOUT]: {
            color: "#800080",
            text: "TIMEOUT"
        },
        [jobStateEnum.NODE_FAIL]: {
            color: "#8B0000",
            text: "NODE FAIL"
        },
        [jobStateEnum.PREEMPTED]: {
            color: "#FFC0CB",
            text: "PREEMPTED"
        },
        [jobStateEnum.BOOT_FAIL]: {
            color: "#A9A9A9",
            text: "BOOT FAIL"
        },
        [jobStateEnum.DEADLINE]: {
            color: "#FFB6C1",
            text: "DEADLINE"
        },
        [jobStateEnum.OOM]: {
            color: "#ADD8E6",
            text: "OOM"
        },
    }

    const jobStateOptions = Object.keys(jobStateMap).map(key => ({ label: jobStateMap[Number(key)].text, value: key }));

    const [isCancelingKey, setIsCancelingKey] = useState<number | string | undefined>(undefined);
    const cancelJob = async (jobId: number | string, cluster?: string) => {
        setIsCancelingKey(jobId);
        try {
            const res = await jobApi.cancelJob(jobId, cluster);
            if (res?.ok) {
                message.success(t('job_list.cancel_success'));
                const values = await form.validateFields();
                fetchData({
                    ...values,
                    page: pagination.current,
                    pageSize: pagination.pageSize,
                    cluster: showClusterPicker ? clusterId : undefined,
                });
            } else {
                message.error(res?.message ?? t('job_list.cancel_failed'));
            }
        }
        finally {
            setIsCancelingKey(undefined);
        }

    }

    const handleOpenWorkDir = (workDir: string) => {
        // 跳转到主页面并展开高亮文件夹
        navigate(`/?workDir=${encodeURIComponent(workDir)}`);
    }

    return (
        <div className='h-full w-full px-3 flex flex-col bg-neutral-light dark:bg-[#1E1E1E]'>
            {/* TODO: Complete date&state search */}
            <div className='flex justify-between items-center py-3'>
                <Form form={form} layout="inline" onFinish={onFinish}>
                    {showClusterPicker && (
                        <Form.Item label={t('job_list.cluster')}>
                            <Select
                                style={{ width: 180 }}
                                value={clusterId}
                                onChange={(value) => {
                                    setClusterId(value);
                                    // A different cluster is a different history; page 3 of
                                    // one is rarely page 3 of the other.
                                    setPagination(prev => ({ ...prev, current: 1 }));
                                }}
                                options={[
                                    { label: t('job_list.all_clusters'), value: 'all' },
                                    ...clusters.map(c => ({ label: c.displayName, value: c.id })),
                                ]}
                            />
                        </Form.Item>
                    )}
                    <Form.Item name="jobName" label={t('job_list.job_name_label')}>
                        <Input placeholder={t('job_list.job_name_placeholder')} />
                    </Form.Item>
                    <Form.Item name="startTime" label={t('job_list.start_time')}>
                        <DatePicker />
                    </Form.Item>
                    <Form.Item name="endTime" label={t('job_list.end_time')}>
                        <DatePicker />
                    </Form.Item>
                    <Form.Item name="state" label={t('job_list.state')}>
                        <Select style={{ width: 180 }} options={jobStateOptions}></Select>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">{t('job_list.search')}</Button>
                    </Form.Item>
                    <Form.Item>
                        <Button icon={<ReloadOutlined />} onClick={() => {
                            form.validateFields().then(values => {
                                fetchData({ ...values, page: pagination.current, pageSize: pagination.pageSize });
                            });
                        }} />
                    </Form.Item>
                </Form>
            </div>
            <ClusterOverviewPanel />
            <Table
                dataSource={data}
                pagination={{ ...pagination, total: total, showSizeChanger: false }}
                onChange={handleTableChange}
                rowKey={(record) => record.jobUid || `${record.cluster || 'local'}:${record.idJob}`}
            >
                <Table.Column title={'ID'} dataIndex="idJob" key="idJob" />
                <Table.Column title={t('job_list.job_name_label')} dataIndex="jobName" key="jobName" />
                <Table.Column title={t('job_list.cpu_cores')} dataIndex="cpuCores" key="cpuCores" />
                <Table.Column title={t('job_list.node')} dataIndex="nodelist" key="nodelist" />
                <Table.Column title={t('job_list.partition')} dataIndex="partition" key="partition" />
                <Table.Column title={t('job_list.node_list')} dataIndex="nodeList" key="nodeList" />
                <Table.Column title={t('job_list.time_start')} dataIndex="timeStart" key="timeStart"
                    render={(value) => {
                        return value ? dayjs(value * 1000).format('YYYY-MM-DD HH:mm:ss') : '-';
                    }}
                />
                <Table.Column title={t('job_list.time_end')} dataIndex="timeEnd" key="timeEnd"
                    render={(value) => {
                        return value ? dayjs(value * 1000).format('YYYY-MM-DD HH:mm:ss') : '-';
                    }}
                />
                <Table.Column
                    title={t('job_list.state')}
                    dataIndex="state"
                    key="state"
                    render={(state) => {
                        return jobStateMap[state] ? <Tag color={jobStateMap[state].color}>{jobStateMap[state].text}</Tag> : <></>;
                    }}
                />
                <Table.Column
                    title={t('job_list.results')}
                    key="results"
                    render={(_, record: jobEnity) => renderResults(record)}
                />
                <Table.Column
                    title={t('job_list.actions')}
                    key="actions"
                    width={250}
                    render={(_, record: jobEnity) => (
                        <Space>
                            {(record.state === jobStateEnum.RUNNING || record.state === jobStateEnum.PENDING) ? <Button loading={isCancelingKey === record.idJob} type="text" onClick={async () => await cancelJob(record.idJob, record.cluster)}>{t('job_list.cancel')}</Button> : <></>}
                            {(!(record.jobName as string).endsWith(".sh")) && <Button type="text" onClick={() => handleOpenWorkDir(record.workDir)}><FolderOpenOutlined /></Button>}
                        </Space>
                    )}
                />
            </Table>
        </div>
    );
};

export default JobList;
